import { createFileRoute, Link } from '@tanstack/react-router';
import { PublicHeader } from '@/components/public/public-header';
import { PublicFooter } from '@/components/public/public-footer';
import { useRouteMeta } from '@/lib/use-route-meta';

export const Route = createFileRoute('/como-funciona')({ component: ComoFuncionaPage });

const STEPS = [
  { number: '01', title: 'Encontre seu profissional', description: 'Navegue pelos perfis de profissionais, filtre por especialidade, modalidade e preço. Leia avaliações de outros clientes e escolha quem melhor combina com você.', tone: 'teal' as const },
  { number: '02', title: 'Escolha o horário', description: 'Veja a agenda real do profissional e selecione um horário disponível. Online ou presencial — você decide o formato da sessão.', tone: 'coral' as const },
  { number: '03', title: 'Realize o pagamento', description: 'Pague com segurança via PIX ou cartão de crédito, por plataformas de pagamento homologadas pelo Banco Central. O agendamento é confirmado automaticamente assim que o pagamento é processado.', tone: 'teal' as const },
  { number: '04', title: 'Participe da sessão', description: 'Para sessões online, o link do Google Meet é gerado automaticamente. Para sessões presenciais, o endereço fica disponível no seu painel. Simples assim.', tone: 'coral' as const },
];

const FAQS = [
  { q: 'Quanto tempo dura uma sessão?', a: 'O tempo de sessão varia por profissional, geralmente entre 50 e 60 minutos. Você verá a duração no perfil antes de agendar.' },
  { q: 'Posso mudar o horário depois de agendar?', a: 'Sim. É possível reagendar até 2 vezes, com pelo menos 3 horas de antecedência.' },
  { q: 'O pagamento é seguro?', a: 'Sim. Utilizamos plataformas de pagamento homologadas pelo Banco Central, com padrões bancários de segurança e criptografia de ponta a ponta.' },
  { q: 'E se eu não gostar do profissional?', a: 'Você pode cancelar sessões futuras e escolher outro profissional. Reembolsos seguem a política de cada profissional.' },
];

function ComoFuncionaPage() {
  useRouteMeta({
    title: 'Como funciona — 4 passos pra começar a terapia',
    description: 'Encontre, agende, pague e participe. Veja o passo a passo pra começar sua terapia online ou presencial com o profissional certo pra você.',
    canonical: 'https://nossoello.com.br/como-funciona',
  });
  return (
    <div className="bg-[#FBF8F3] min-h-screen flex flex-col">
      <PublicHeader />
      <main className="flex-1">
        <section className="max-w-4xl mx-auto px-6 pt-16 pb-12 text-center">
          <div className="inline-flex items-center gap-2 bg-teal-50 border border-teal-100 rounded-full px-4 py-1.5 text-teal-700 text-sm font-medium mb-6">
            Simples do início ao fim
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-800 leading-tight tracking-tight">
            Como funciona a Nosso Ello
          </h1>
          <p className="text-lg text-slate-500 mt-4 max-w-2xl mx-auto leading-relaxed">
            Do primeiro clique à primeira sessão, em quatro passos. Sem burocracia, sem complicação.
          </p>
        </section>

        <section className="max-w-5xl mx-auto px-6 pb-20">
          <div className="grid md:grid-cols-2 gap-5">
            {STEPS.map((step) => <StepCard key={step.number} {...step} />)}
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-6 pb-20">
          <div className="bg-gradient-to-r from-teal-400 to-coral-400 rounded-[28px] p-8 md:p-12 text-white text-center">
            <h2 className="text-2xl md:text-3xl font-extrabold mb-3">Tudo no mesmo lugar</h2>
            <p className="text-white/90 text-base max-w-xl mx-auto leading-relaxed">
              Agenda, pagamento, histórico de sessões e link do Meet — tudo acessível pelo seu painel, no computador ou celular.
            </p>
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              {['Agenda online', 'Pagamento seguro', 'Google Meet integrado', 'Histórico de sessões'].map((item) => (
                <span key={item} className="bg-white/20 border border-white/30 rounded-full px-4 py-1.5 text-sm font-medium">
                  {item}
                </span>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-3xl mx-auto px-6 pb-20">
          <h2 className="text-2xl font-extrabold text-slate-800 mb-8 text-center">Dúvidas frequentes</h2>
          <div className="space-y-4">
            {FAQS.map((faq) => (
              <div key={faq.q} className="bg-white rounded-2xl border border-slate-100 px-6 py-5 shadow-sm">
                <h3 className="font-semibold text-slate-800 mb-1.5">{faq.q}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-6 pb-20 text-center">
          <h2 className="text-2xl font-extrabold text-slate-800 mb-3">Pronto para começar?</h2>
          <p className="text-slate-500 mb-8">Encontre seu profissional e agende sua primeira sessão hoje.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/terapeutas" className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-3 rounded-full font-medium transition shadow-sm">
              Ver profissionais →
            </Link>
            <Link to="/register" className="bg-coral-400 hover:bg-coral-500 text-white px-8 py-3 rounded-full font-medium transition shadow-sm">
              Criar conta grátis
            </Link>
          </div>
        </section>
      </main>
      <PublicFooter />
    </div>
  );
}

function StepCard({ number, title, description, tone }: { number: string; title: string; description: string; tone: 'teal' | 'coral' }) {
  const isTeal = tone === 'teal';
  return (
    <div className={`rounded-[28px] p-8 shadow-sm ${isTeal ? 'bg-teal-300' : 'bg-coral-300'}`}>
      <div className={`text-5xl font-extrabold mb-4 ${isTeal ? 'text-teal-500/30' : 'text-coral-500/30'}`}>
        {number}
      </div>
      <h3 className="text-xl font-bold text-white mb-3">{title}</h3>
      <p className="text-white/90 text-sm leading-relaxed">{description}</p>
    </div>
  );
}
